// React
import React, { createRef } from 'react';
// External
import { Form } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import getFormFieldVal from 'utils/getFormFieldVal';
import isNull from 'lodash/isNull';
// Styles
import './styles.scss';

class CustomTextarea extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentLines: 0,
            currentCharacters: 0,
            maxLines: this.props.maxLines ?? 1,
            maxCharacters: this.props.maxCharacters ?? 1,
        };
        this.text = props.value;
        this.textAreaRef = createRef();
    }

    /**
     * Lifecycle method called after the component has been inserted into the DOM.
     * Invokes the triggerChange method.
     * 
     * @returns {void}
     */
    componentDidMount() {
        this.triggerChange();
    }

    /**
     * Triggers the change event for the component if the value prop is present.
     * Invokes the onChange method with a mock event containing the current value.
     *
     * @returns {void}
     */
    triggerChange() {
        if (!!this.props.value) {
            const mockEvent = {
                'target': {
                    'name': 'engraving-text',
                    'value': this.text,
                },
                'currentTarget': this.textAreaRef.current,
            };

            this.onChange(mockEvent);
        }
    }

    /**
     * Update the text for this component and trigger the prop onChange method
     * 
     * @param {String} textValue 
     * 
     * @returns {void}
     */
    updateText(textValue) {
        this.text = textValue;
        this.props.onChange(textValue);
    }

    /**
     * Triggers the input validation and checks the remaining available input
     * 
     * @param {Event} event 
     * 
     * @returns {void}
     */
    onChange(event) {
        const strEngravingText = getFormFieldVal(event)['engraving-text'];

        this.validateInput(strEngravingText, event.currentTarget);
        this.checkRemainingInput(event);
    }

    /**
     * Checks the remaining available input based on the current text values
     * 
     * @param {Event} event 
     * 
     * @returns {void}
     */
    checkRemainingInput(event) {
        // Split the text rows into separate lines
        const arLines = this.text.split(/\r|\r\n|\n/);

        // Get line count
        const intLineCount = arLines.length;

        // get the position of the newly added char - starts with 0
        const intRecentInputtedPosition = event.currentTarget.selectionStart;

        // initialise total length of the engraving
        let intAccumulatedLength = 0;

        arLines.forEach(strLine => {
            // total length per line
            const intContentLength = strLine.length;

            // accumulate length
            intAccumulatedLength += intContentLength;

            // get the difference to get the staring point of the line
            const intDifference = intAccumulatedLength - intContentLength;

            // count inputted characters
            const intInputtedCharCount = strLine.length;

            // check which line are we inputting on
            if (intAccumulatedLength >= intRecentInputtedPosition && intDifference <= intRecentInputtedPosition) {
                this.setState({ currentLines: intLineCount, currentCharacters: intInputtedCharCount });
            }
            
            // +1 for the new line
            intAccumulatedLength++;
        })
    }

    /**
     * Validates the input and trims it if it exceeds the max input limits
     * 
     * @param {String} textValue 
     * @param {object} element
     * 
     * @returns {void}
     */
    validateInput(textValue, element) {
        const lines = textValue.split("\n");
        let endPosition = null;

        for (let i = 0; i < lines.length; i++) {
            // If the current line exceeds the character limit, cut the text (excluding whitespace)
            if (lines[i].length > this.state.maxCharacters) {
                let intCounter = 0;

                for (let j = 0; j < lines[i].length; j++) {
                    if (intCounter === this.state.maxCharacters) {
                        // Get the end position of the current line
                        endPosition = ((this.state.maxCharacters * (i + 1)) + i);
                        lines[i] = lines[i].substring(0, j);

                        break;
                    }

                    intCounter++;
                }
            }
        }

        // Truncate if the text exceeds the max line limit
        while (lines.length > this.state.maxLines) {
            lines.pop();
        }

        this.updateText(lines.join("\n"));

        // If there is an end position (there's a line that exceeds limit), reset the caret's position to the end position
        if (!isNull(endPosition)) {
            setTimeout(() => {
                element.setSelectionRange(endPosition, endPosition);
            }, 10)
        }
    }

    render() {
        return (
            <div className={`custom-textarea ${this.props.className} ${this.props.hasError() ? 'is-invalid' : ''}`}>
                <Form.Control 
                    ref={this.textAreaRef}
                    as={'textarea'} 
                    name={this.props.name}
                    onChange={e => this.onChange(e)}
                    className="custom-textarea__field"
                    value={this.text}
                    onKeyUp={e => this.checkRemainingInput(e)}
                    onMouseUp={e => this.checkRemainingInput(e)}
                />
                <div className="custom-textarea__limit">{ `${this.state.currentLines}/${this.state.maxLines} lines and ${this.state.currentCharacters}/${this.state.maxCharacters} characters used` }</div>
            </div>
        );
    }
}

export default withRouter(CustomTextarea);
